import { quaternionMultiply } from '@sb/geometry/Quaternion';
import type { Space } from '@sb/routine-runner';
import type {
  PalletBoxInstance,
  PalletBoxLayerPattern,
  PalletBoxType,
} from '@sb/routine-runner/types/PalletizingTypes';

const millimetersToMeters = (millimeters: number): number => {
  return millimeters * 0.001;
};

/**
 * Rotates a point around a center by a given angle in degrees.
 * This is the same logic used in PalletBoxes.tsx for consistent coordinate transformation.
 *
 * @param point - The point to rotate
 * @param center - The center point to rotate around
 * @param angleDegrees - The rotation angle in degrees
 * @returns The rotated point coordinates
 */
function rotatePoint(
  point: [number, number, number],
  center: [number, number, number],
  angleDegrees: number,
): [number, number, number] {
  const angleRad = -(angleDegrees * Math.PI) / 180;
  const dx = point[0] - center[0];
  const dy = point[1] - center[1];

  return [
    center[0] + dx * Math.cos(angleRad) + dy * Math.sin(angleRad),
    center[1] - dx * Math.sin(angleRad) + dy * Math.cos(angleRad),
    point[2],
  ];
}

function qNorm(q: { w: number; i: number; j: number; k: number }) {
  const magnitude = Math.hypot(q.w, q.i, q.j, q.k);

  if (magnitude === 0) return q;

  return {
    w: q.w / magnitude,
    i: q.i / magnitude,
    j: q.j / magnitude,
    k: q.k / magnitude,
  };
}

function qAxisAngle(
  axis: [number, number, number],
  angle: number,
): { w: number; i: number; j: number; k: number } {
  const half = angle / 2;
  const s = Math.sin(half);

  return qNorm({
    w: Math.cos(half),
    i: axis[0] * s,
    j: axis[1] * s,
    k: axis[2] * s,
  });
}

export function mirrorLayerPattern(
  layerPattern: PalletBoxLayerPattern,
  palletBase: Space.PalletBase,
  boxType: PalletBoxType,
  mirrorDirection: 'x' | 'y',
): PalletBoxLayerPattern {
  const getBoxWidth = (box: PalletBoxInstance) => {
    return box.rotation === 0 || box.rotation === 180
      ? boxType.width
      : boxType.depth;
  };

  const getBoxDepth = (box: PalletBoxInstance) => {
    return box.rotation === 0 || box.rotation === 180
      ? boxType.depth
      : boxType.width;
  };

  const getApproachDirection = (approachDirection: number) => {
    return mirrorDirection === 'x'
      ? Math.abs(360 - approachDirection)
      : (180 - approachDirection + 360) % 360;
  };

  return {
    ...layerPattern,
    globalApproachDirection: getApproachDirection(
      layerPattern.globalApproachDirection,
    ),
    boxList: layerPattern.boxList.map((box) => {
      return {
        ...box,
        x:
          mirrorDirection === 'x'
            ? palletBase.widthMM - box.x - getBoxWidth(box)
            : box.x,
        y:
          mirrorDirection === 'y'
            ? palletBase.lengthMM - box.y - getBoxDepth(box)
            : box.y,
        approachDirection: getApproachDirection(box.approachDirection),
      };
    }),
  };
}

/**
 * Calculates the pickup location for a specific box instance.
 *
 * The pickup location is calculated as follows:
 * - X and Y coordinates are based on the box's position in the layer plus any
 *   pickup position offsets defined in the box type, properly accounting for
 *   pallet position and rotation
 * - Z coordinate is based on the layer's base height plus the full box height (top center)
 * - Rotation combines the box instance rotation with the pickup location's Z-axis rotation
 *
 * @param boxInstance - The box instance to calculate pickup location for
 * @param boxType - The box type definition
 * @param layerBaseHeight - The base height of the layer this box is in
 * @param palletBase - The pallet base to get position and rotation information
 * @returns The calculated pickup location
 */
function calculateBoxPickupLocation(
  boxInstance: PalletBoxInstance,
  boxType: PalletBoxType,
  layerBaseHeight: number,
  palletBase: Space.PalletBase,
  zOffsetMM: number = 0,
): Space.Position {
  let pickupX =
    -1 *
      (boxInstance.x +
        (boxInstance.rotation === 0 || boxInstance.rotation === 180
          ? boxType.width
          : boxType.depth) /
          2) +
    palletBase.widthMM;

  let pickupY =
    boxInstance.y +
    (boxInstance.rotation === 0 || boxInstance.rotation === 180
      ? boxType.depth
      : boxType.width) /
      2;

  const pickupZ = layerBaseHeight + boxType.height + zOffsetMM;

  const widthOffset = boxType.pickupPositionWidthOffset ?? 0;
  const depthOffset = boxType.pickupPositionDepthOffset ?? 0;

  if (boxInstance.rotation === 0) {
    pickupX += widthOffset;
    pickupY += depthOffset;
  } else if (boxInstance.rotation === 90) {
    pickupX -= depthOffset;
    pickupY += widthOffset;
  } else if (boxInstance.rotation === 180) {
    pickupX -= widthOffset;
    pickupY -= depthOffset;
  } else if (boxInstance.rotation === 270) {
    pickupX += depthOffset;
    pickupY -= widthOffset;
  }

  const pickupPositionInMeters: [number, number, number] = [
    millimetersToMeters(pickupX),
    millimetersToMeters(pickupY),
    millimetersToMeters(pickupZ),
  ];

  const palletCornerPosition: [number, number, number] = [
    millimetersToMeters(palletBase.cornerXMM),
    millimetersToMeters(palletBase.cornerYMM),
    millimetersToMeters(palletBase.heightMM / 2),
  ];

  // Adjust pickup position based on cornerToPosition setting
  let adjustedPickupX = pickupPositionInMeters[0];
  const adjustedPickupY = pickupPositionInMeters[1];

  if (palletBase.cornerToPosition === 'left') {
    adjustedPickupX =
      pickupPositionInMeters[0] - millimetersToMeters(palletBase.widthMM);
  }

  const translatedPosition: [number, number, number] = [
    palletCornerPosition[0] + adjustedPickupX,
    palletCornerPosition[1] + adjustedPickupY,
    palletCornerPosition[2] + pickupPositionInMeters[2],
  ];

  const rotatedPosition = rotatePoint(
    translatedPosition,
    palletCornerPosition,
    palletBase.cornerYawDegrees,
  );

  const pickupRotation =
    ((boxInstance.rotation + 90 + palletBase.cornerYawDegrees) * Math.PI) / 180;

  const rotationQuaternion = qAxisAngle([0, 0, 1], pickupRotation);
  const downwardQuaternion = qAxisAngle([0, 1, 0], Math.PI / 2);

  const quaternion = qNorm(
    quaternionMultiply(rotationQuaternion, downwardQuaternion),
  );

  return {
    name: `pickup_${boxInstance.id}`,
    jointAngles: [0, 0, 0, 0, 0, 0], // 6 joint angles set to 0 since we don't use them
    pose: {
      x: rotatedPosition[0],
      y: rotatedPosition[1],
      z: rotatedPosition[2],
      ...quaternion,
    },
    tcpOption: undefined,
    wristPose: undefined,
  };
}

/**
 * Calculates pickup locations for all boxes in a pallet arrangement.
 *
 * @param palletBoxes - The pallet boxes space item containing box types and layer patterns
 * @param allSpaceItems - Array of all space items to lookup pallet base height and pickup location.
 * It needs to include the pallet base and the pickup location.
 * @returns A map from pallet box id to its calculated pickup location
 */

export function getPickupLocations(
  palletBoxes: Space.PalletBoxes,
  allSpaceItems: Space.Item[],
  palletBaseID: string,
  noCustomPositions?: boolean,
  zOffsetMM: number = 0,
): Map<string, Space.Position> {
  const pickupLocations = new Map<string, Space.Position>();

  if (!palletBoxes.layerOrder || !palletBoxes.layerPatterns) {
    return pickupLocations;
  }

  if (!palletBoxes.palletBaseIDs.includes(palletBaseID)) {
    return pickupLocations;
  }

  const palletBase = allSpaceItems.find(
    (item): item is Space.PalletBase =>
      item.kind === 'palletBase' && item.id === palletBaseID,
  );

  if (!palletBase) {
    return pickupLocations;
  }

  // Start with floor offset + pallet height/2
  let currentHeight =
    palletBase.floorToPalletDistanceMM + palletBase.heightMM / 2;

  for (let i = 0; i < palletBoxes.layerOrder.length; i += 1) {
    const layerId = palletBoxes.layerOrder[i];
    let layer = palletBoxes.layerPatterns.find((l) => l.id === layerId);
    if (!layer) continue;

    const boxType = palletBoxes.boxTypes.find(
      (bt) => bt.id === layer?.boxTypeID,
    );

    if (!boxType) continue;

    const palletBaseIDIndex = palletBoxes.palletBaseIDs.indexOf(palletBase.id);
    if (palletBaseIDIndex === -1) continue;

    if (palletBaseIDIndex % 2 === 1) {
      layer = mirrorLayerPattern(layer, palletBase, boxType, 'x');
    }

    const pickupLocationPose = boxType.pickupPosition?.pose;
    if (!pickupLocationPose) continue;

    for (const boxInstance of layer.boxList) {
      const pickupLocation = calculateBoxPickupLocation(
        boxInstance,
        boxType,
        currentHeight,
        palletBase,
        zOffsetMM,
      );

      pickupLocations.set(`${layerId}/${boxInstance.id}/${i}`, pickupLocation);
    }

    currentHeight += boxType.height;
  }

  if (!noCustomPositions) {
    const customPickupPositionsForPalletBase =
      palletBoxes.customPositions?.pickupPositions[palletBase.id] ?? {};

    for (const palletBoxKey of Object.keys(
      customPickupPositionsForPalletBase,
    )) {
      const pickupLocation = customPickupPositionsForPalletBase[palletBoxKey];
      if (!pickupLocation) continue;
      pickupLocations.set(palletBoxKey, pickupLocation);
    }
  }

  return pickupLocations;
}

export function getApproachLocations(
  palletBoxes: Space.PalletBoxes,
  allSpaceItems: Space.Item[],
  palletBaseID: string,
  noCustomPositions?: boolean,
): Map<string, Space.Position> {
  const shiftedPalletBoxesForApproach = palletBoxes.layerPatterns.map(
    (layer) => {
      return {
        ...layer,
        boxList: layer.boxList.map((box) => {
          return {
            ...box,
            x:
              box.x +
              palletBoxes.approachDistanceXYMM *
                Math.cos(((box.approachDirection - 90) * Math.PI) / 180),
            y:
              box.y +
              palletBoxes.approachDistanceXYMM *
                Math.sin(((box.approachDirection - 90) * Math.PI) / 180),
          };
        }),
      };
    },
  );

  const approachLocations = getPickupLocations(
    {
      ...palletBoxes,
      layerPatterns: shiftedPalletBoxesForApproach,
    },
    allSpaceItems,
    palletBaseID,
    true,
    palletBoxes.approachDistanceZMM,
  );

  if (!noCustomPositions) {
    const customApproachPositionsForPalletBase =
      palletBoxes.customPositions?.approachPositions[palletBaseID] ?? {};

    for (const palletBoxKey of Object.keys(
      customApproachPositionsForPalletBase,
    )) {
      const approachLocation =
        customApproachPositionsForPalletBase[palletBoxKey];

      if (!approachLocation) continue;
      approachLocations.set(palletBoxKey, approachLocation);
    }
  }

  return approachLocations;
}

export function getPickupLocationsInOrder(
  palletBoxes: Space.PalletBoxes,
  allSpaceItems: Space.Item[],
  palletBaseID: string,
  useApproachLocations?: boolean,
  noCustomPositions?: boolean,
): Space.Position[] {
  const pickupLocations =
    useApproachLocations === true
      ? getApproachLocations(
          palletBoxes,
          allSpaceItems,
          palletBaseID,
          noCustomPositions,
        )
      : getPickupLocations(
          palletBoxes,
          allSpaceItems,
          palletBaseID,
          noCustomPositions,
        );

  return palletBoxes.layerOrder
    .map((layerId, index) => {
      const layer = palletBoxes.layerPatterns.find((l) => l.id === layerId);
      if (!layer) return [];

      return layer.boxList
        .sort((a, b) => a.order - b.order)
        .map((box) => {
          const pickupLocation = pickupLocations.get(
            `${layerId}/${box.id}/${index}`,
          );

          if (!pickupLocation) return null;

          return {
            ...pickupLocation,
          };
        });
    })
    .flat()
    .filter((location): location is Space.Position => location !== null);
}
